import { useEffect, useState } from "react";
import { ArrowLeft, Building2, ChevronRight, MapPin, Mountain, Plus, X } from "lucide-react";
import { createSession, listSessions } from "@/offline/sessionsRepo";
import type { ClimbSessionOut } from "@/types/api";
import { formatDate } from "@/utils/dates";
import { useRecentLocations } from "@/hooks/useRecentLocations";
import { useOutdoorLocations } from "@/hooks/useOutdoorLocations";
import { LocationInput } from "@/components/sessions/LocationInput";
import { cn } from "@/utils/cn";
import { getErrorMessage } from "@/utils/apiError";

interface NewSessionModalProps {
  onClose: () => void;
  onCreated: (sessionId: number) => void;
}

type View = "choice" | "new" | "existing";

export function NewSessionModal({ onClose, onCreated }: NewSessionModalProps) {
  const [view, setView] = useState<View>("choice");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="modal-panel w-full max-w-sm p-6 max-h-[85dvh] flex flex-col">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            {view !== "choice" && (
              <button onClick={() => setView("choice")} className="text-text-muted hover:text-text-primary">
                <ArrowLeft size={18} />
              </button>
            )}
            <h2 className="font-display text-2xl tracking-wide">
              {view === "new" ? "NEW SESSION" : view === "existing" ? "OPEN SESSION" : "LOG"}
            </h2>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary">
            <X size={20} />
          </button>
        </div>

        {view === "choice" && <ChoiceView onNew={() => setView("new")} onExisting={() => setView("existing")} />}
        {view === "new" && <NewSessionForm onCreated={onCreated} />}
        {view === "existing" && <ExistingSessionPicker onPick={onCreated} />}
      </div>
    </div>
  );
}

function ChoiceView({ onNew, onExisting }: { onNew: () => void; onExisting: () => void }) {
  return (
    <div className="flex flex-col gap-3">
      <button
        onClick={onNew}
        className="flex items-center gap-4 p-4 rounded-xl border border-white/10 hover:bg-white/5 transition-colors text-left"
        data-testid="new-session-choice"
      >
        <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
          <Plus size={20} className="text-accent" />
        </div>
        <div>
          <p className="text-text-primary font-semibold text-sm">New Session</p>
          <p className="text-text-muted text-xs">Start a fresh climbing session</p>
        </div>
      </button>

      <button
        onClick={onExisting}
        className="flex items-center gap-4 p-4 rounded-xl border border-white/10 hover:bg-white/5 transition-colors text-left"
      >
        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0">
          <ChevronRight size={20} className="text-text-muted" />
        </div>
        <div>
          <p className="text-text-primary font-semibold text-sm">Open Existing</p>
          <p className="text-text-muted text-xs">Add routes to a previous session</p>
        </div>
      </button>
    </div>
  );
}

function NewSessionForm({ onCreated }: { onCreated: (id: number) => void }) {
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);
  const [sessionType, setSessionType] = useState<"gym" | "outdoor" | null>(null);
  // Gym
  const [gymName, setGymName] = useState("");
  // Outdoor
  const [area, setArea] = useState("");
  const [cragName, setCragName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const recentGyms = useRecentLocations("gym");
  const { areas: recentAreas, cragsForArea } = useOutdoorLocations();

  const filteredGyms = recentGyms.filter(
    (loc) => gymName === "" || loc.toLowerCase().includes(gymName.toLowerCase())
  );
  const filteredAreas = recentAreas.filter(
    (a) => area === "" || a.toLowerCase().includes(area.toLowerCase())
  );
  const filteredCrags = cragsForArea(area).filter(
    (c) => cragName === "" || c.toLowerCase().includes(cragName.toLowerCase())
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const session = await createSession(
        sessionType === "outdoor"
          ? { session_date: date, session_type: "outdoor", area, crag_name: cragName }
          : { session_date: date, session_type: "gym", gym_name: gymName }
      );
      onCreated(session.id);
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Could not create session."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Gym / Outdoor toggle */}
      <div>
        <div className="flex gap-1 p-1 bg-white/5 rounded-xl">
          <button
            type="button"
            data-testid="new-session-type-gym"
            onClick={() => setSessionType("gym")}
            className={cn(
              "flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors",
              sessionType === "gym" ? "bg-accent text-white" : "text-text-muted hover:text-text-primary"
            )}
          >
            <Building2 size={15} /> Gym
          </button>
          <button
            type="button"
            onClick={() => setSessionType("outdoor")}
            className={cn(
              "flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors",
              sessionType === "outdoor" ? "bg-accent text-white" : "text-text-muted hover:text-text-primary"
            )}
          >
            <Mountain size={15} /> Outdoor
          </button>
        </div>
        {sessionType === null && (
          <p className="text-text-muted/60 text-xs mt-1.5 text-center">Select gym or outdoor to continue</p>
        )}
      </div>

      <div>
        <label
          htmlFor="new-session-date"
          className="text-xs text-text-muted uppercase tracking-widest block mb-1.5"
        >
          Date
        </label>
        <input
          id="new-session-date"
          name="date"
          type="date"
          className="input"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          required
        />
      </div>

      {/* Gym fields */}
      {sessionType === "gym" && (
        <LocationInput
          label="Gym"
          value={gymName}
          onChange={setGymName}
          placeholder="Movement, Brooklyn Boulders…"
          historySuggestions={filteredGyms}
          liveSearch
        />
      )}

      {/* Outdoor fields */}
      {sessionType === "outdoor" && (
        <>
          <LocationInput
            label="Area"
            value={area}
            onChange={(v) => { setArea(v); setCragName(""); }}
            placeholder="Red River Gorge, Yosemite…"
            historySuggestions={filteredAreas}
          />
          <LocationInput
            label="Crag"
            value={cragName}
            onChange={setCragName}
            placeholder="Military Wall, El Capitan…"
            historySuggestions={filteredCrags}
          />
        </>
      )}

      {error && (
        <div className="bg-red-400/10 border border-red-400/20 rounded-xl p-3">
          <p className="text-red-400 text-sm">🧗 {error}</p>
        </div>
      )}

      <button
        type="submit"
        className="btn-primary"
        disabled={loading || sessionType === null}
        data-testid="new-session-submit"
      >
        {loading ? "Creating…" : "Start Session"}
      </button>
    </form>
  );
}

function ExistingSessionPicker({ onPick }: { onPick: (id: number) => void }) {
  const [sessions, setSessions] = useState<ClimbSessionOut[] | null>(null);

  useEffect(() => {
    listSessions().then(setSessions);
  }, []);

  if (sessions === null) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  if (sessions.length === 0) {
    return <p className="text-text-muted text-sm text-center py-8">No sessions yet.</p>;
  }

  return (
    <div className="flex flex-col gap-2 overflow-y-auto">
      {sessions.map((s) => (
        <button
          key={s.id}
          onClick={() => onPick(s.id)}
          className="flex items-center gap-3 p-3 rounded-xl border border-white/10 hover:bg-white/5 transition-colors text-left"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              {s.session_type === "gym" ? (
                <Building2 size={11} className="text-text-muted shrink-0" />
              ) : s.session_type === "outdoor" ? (
                <Mountain size={11} className="text-text-muted shrink-0" />
              ) : s.gym_name ? (
                <MapPin size={11} className="text-text-muted shrink-0" />
              ) : null}
              <p className="text-text-primary text-sm font-medium truncate">
                {s.session_type === "outdoor"
                  ? s.crag_name
                    ? `${s.area ? `${s.area} · ` : ""}${s.crag_name}`
                    : (s.area ?? s.gym_name ?? "Session")
                  : (s.gym_name ?? "Session")}
              </p>
            </div>
            <p className="text-text-muted text-xs">
              {formatDate(s.session_date)} · {s.routes.length} route{s.routes.length !== 1 ? "s" : ""}
            </p>
          </div>
          <ChevronRight size={16} className="text-text-muted shrink-0" />
        </button>
      ))}
    </div>
  );
}
